/**
 * Self-playing stepped story. Plays once when the section scrolls into view:
 * each step is rendered on a timer (lines accumulate, images stack onto the
 * pile), then it rests on the final step (which reveals the CTA + footnote).
 * Time-driven, not scroll-driven — the page scrolls normally past it. Honours
 * prefers-reduced-motion by jumping straight to the final step.
 */
class AnimatedSteps extends HTMLElement {
  connectedCallback() {
    this.lines = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.animated-steps__line')]);
    this.cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.animated-steps__card')]);
    this.count = this.lines.length;
    if (!this.count) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.duration = Number(this.dataset.stepDuration) || 2200;
    this.activeIndex = -1;
    this.timer = null;
    this.played = false;

    if (this.reduced || this.count < 2) {
      // Nothing to play through — show the final (complete) state.
      this.#render(this.count - 1);
      return;
    }

    // Keep every line hidden until the section is on screen — revealing step 0
    // at connect time (before the section scrolls into view) made line 1 appear
    // early, so the gap before line 2 felt long and lines 2→3 then felt rushed.
    // Play once, the first time the section is meaningfully on screen.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.observer.disconnect();
          this.#play();
        }
      },
      { threshold: 0.3 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.timer) clearTimeout(this.timer);
  }

  /** Step through the story on a timer, then rest on the final step. */
  #play() {
    if (this.played) return;
    this.played = true;

    this.#render(0);
    let index = 0;
    const advance = () => {
      index += 1;
      if (index >= this.count) return; // done — hold the final step
      this.#render(index);
      this.timer = setTimeout(advance, this.duration);
    };
    this.timer = setTimeout(advance, this.duration);
  }

  /**
   * Apply the visual state for a given step index.
   * @param {number} index
   */
  #render(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;

    this.style.setProperty('--active', String(index));
    // Lines accumulate: every line up to the current step stays visible, earlier
    // ones muted, the current one emphasised.
    this.lines.forEach((line, i) => {
      line.classList.toggle('is-shown', i <= index);
      line.classList.toggle('is-active', i === index);
    });
    this.cards.forEach((card, i) => {
      card.classList.toggle('is-stacked', i <= index);
      card.classList.toggle('is-top', i === index);
    });
    this.classList.toggle('is-complete', index === this.count - 1);
  }
}

if (!customElements.get('animated-steps')) {
  customElements.define('animated-steps', AnimatedSteps);
}
